/**
 * Pure helpers behind client-ticket triage and the per-org open-ticket cap
 * (Nikhil 2026-09-13). Client-raised tickets arrive assigned to the client's
 * CS rep with triage PENDING; the rep accepts, merges into an existing ticket,
 * or declines with a client-safe reason. Exercised by ticket_triage.test.js
 * without a backend.
 *
 * Contract (cs-analysis, built in parallel — every consumer is defensive):
 *   ticket += triage:'PENDING'|'ACCEPTED'|'MERGED'|'DECLINED', acceptedAt,
 *             acceptedBy, mergedIntoId, mergedIntoRef
 *   GET  /tickets?triage=PENDING
 *   POST /tickets/:id/accept · POST /tickets/:id/merge {intoId} · POST /tickets/:id/decline {reason}
 *   POST /clients/:storeId/ticket-cap { openPerOrg }   (null clears the override)
 *   client (GET /comms/clients rows) += ticketCapOverride: number|null
 */
import { CLIENT_TICKET_PATH } from '../../Chat/user_chat';
import { filter_linkable_tickets } from './task_ticket_links';

export const TRIAGE = { PENDING: 'PENDING', ACCEPTED: 'ACCEPTED', MERGED: 'MERGED', DECLINED: 'DECLINED' };
export const TRIAGE_LABELS = { PENDING: 'Needs triage', ACCEPTED: 'Accepted', MERGED: 'Merged', DECLINED: 'Declined' };
export const NEEDS_TRIAGE_FILTER = 'needs_triage';

export const TRIAGE_PATHS = {
    accept: (id) => `/api/v1/tickets/${encodeURIComponent(String(id))}/accept`,
    merge: (id) => `/api/v1/tickets/${encodeURIComponent(String(id))}/merge`,
    decline: (id) => `/api/v1/tickets/${encodeURIComponent(String(id))}/decline`,
};
export const ticket_cap_path = (store_id) => `/api/v1/clients/${encodeURIComponent(String(store_id))}/ticket-cap`;

/** Same wording as the drawer's status-machine decline — the client reads it. */
export const DECLINE_PROMPT = 'Decline reason (CLIENT-SAFE wording — the client sees this):';

export const is_pending_triage = (t) => !!t && t.triage === TRIAGE.PENDING;
export const is_merged = (t) => !!t && t.triage === TRIAGE.MERGED;
export const pending_triage_rows = (tickets) => (Array.isArray(tickets) ? tickets : []).filter(is_pending_triage);

/** List query for the queue's active pill — only needs-triage is server-side. */
export const triage_query = (filter) => (filter === NEEDS_TRIAGE_FILTER ? { triage: TRIAGE.PENDING } : undefined);

/** Human triage label, '' for rows without a triage state (older backend). */
export const triage_label = (t) => (t && TRIAGE_LABELS[t.triage]) || '';

/** "CT-12" for the merge target; the backend sends the human ref, older rows the bare number. */
export const merged_into_ref = (t) => {
    if (!t || t.mergedIntoRef == null || t.mergedIntoRef === '') return '';
    const raw = String(t.mergedIntoRef).trim();
    if (!raw) return '';
    return /^CT-/i.test(raw) ? raw.toUpperCase() : `CT-${raw}`;
};
export const merged_into_href = (t) => {
    const id = t && t.mergedIntoId != null && String(t.mergedIntoId).trim() ? String(t.mergedIntoId).trim() : null;
    return id ? `${CLIENT_TICKET_PATH}?open=${encodeURIComponent(id)}` : null;
};
export const merged_into_label = (t) => (is_merged(t) ? `Merged into ${merged_into_ref(t) || 'another ticket'}` : '');

/** The org's tickets a pending ticket can be merged into: open (not closed /
 *  declined), already triaged (not pending or merged themselves), not itself;
 *  matched on title / CT-ref / module like the task-link picker. */
export const merge_candidates = (tickets, q, self_id) =>
    filter_linkable_tickets(tickets, q, self_id != null ? [self_id] : [])
        .filter((t) => t.triage !== TRIAGE.PENDING && t.triage !== TRIAGE.MERGED);

const short = (s, n) => { const v = String(s || '').trim(); return v.length > n ? v.slice(0, n - 1) + '…' : v; };

export const merge_confirm_text = (ticket, into) =>
    `Merge CT-${ticket.ref} "${short(ticket.title, 60)}" into CT-${into.ref} "${short(into.title, 60)}"?\n\n`
    + `CT-${ticket.ref} closes as a duplicate and its raiser is pointed to CT-${into.ref}.`;

// ── open-ticket cap ─────────────────────────────────────────────────────────

/** The org's override, or null when the platform default applies. */
export const ticket_cap_of = (client) => {
    const v = client ? client.ticketCapOverride : null;
    return Number.isInteger(v) && v >= 0 ? v : null;
};

export const ticket_cap_label = (client) => {
    const v = ticket_cap_of(client);
    return v == null ? 'platform default' : `${v} open ticket${v === 1 ? '' : 's'} (org override)`;
};

/** Editor input → null (clear the override), a non-negative integer, or undefined when invalid. */
export const parse_cap_input = (s) => {
    const t = String(s == null ? '' : s).trim();
    if (!t) return null;
    if (!/^\d+$/.test(t)) return undefined;
    return Number(t);
};

/** Client row for a store from GET /comms/clients, or null. */
export const client_for_store = (clients, store_id) =>
    (Array.isArray(clients) ? clients : []).find((c) => c && String(c.storeId) === String(store_id)) || null;
